import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Spinner, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { DnsRecord } from "../../../../helpers/sending_setup_helper";
import {
  ensureUiStateLoaded,
  loadDnsCopiedKeys,
  persistDnsCopiedKeys,
} from "../../../../helpers/ui_state_helper";

type Props = {
  records: DnsRecord[];
  delegationMode?: string;
  /** Escopo do “já copiei” (ex.: tracking hostname). */
  storageScope?: string;
  /** Wizard passo A: CTA marca tudo + dispara verificação (LAC-111 polish). */
  wizardVerifyCta?: boolean;
  verifyBusy?: boolean;
  onMarkAllAndVerify?: () => void;
};

function purposeLabel(
  purpose: string,
  t: (key: string) => string
): string {
  if (purpose === "tracking") return t("settings.sending.dns.purpose.tracking");
  if (purpose === "ses") return t("settings.sending.dns.purpose.ses");
  return purpose;
}

function recordOk(status?: string): boolean {
  return ["ok", "verified", "active", "dns_ok", "ssl_ok"].includes(
    String(status || "").toLowerCase()
  );
}

function isPendingDkim(row: DnsRecord): boolean {
  return (
    row.purpose === "ses" &&
    String(row.type).toUpperCase() === "CNAME" &&
    !recordOk(row.status)
  );
}

function recordsAsText(
  records: DnsRecord[],
  t: (key: string) => string
): string {
  const header = [
    t("settings.sending.dns.type"),
    t("settings.sending.dns.name"),
    t("settings.sending.dns.value"),
    t("settings.sending.dns.purpose"),
    t("settings.sending.dns.status"),
  ].join("\t");

  const lines = records.map((row) =>
    [
      row.type,
      row.name,
      row.value,
      purposeLabel(row.purpose, t),
      recordOk(row.status)
        ? t("settings.sending.dns.statusOk")
        : t("settings.sending.dns.statusPending"),
    ].join("\t")
  );

  return [header, ...lines].join("\n");
}

function recordsAsCsv(
  records: DnsRecord[],
  t: (key: string) => string
): string {
  const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
  const header = [
    t("settings.sending.dns.type"),
    t("settings.sending.dns.name"),
    t("settings.sending.dns.value"),
    t("settings.sending.dns.purpose"),
    t("settings.sending.dns.status"),
  ]
    .map(escape)
    .join(",");

  const lines = records.map((row) =>
    [
      row.type,
      row.name,
      row.value,
      purposeLabel(row.purpose, t),
      recordOk(row.status)
        ? t("settings.sending.dns.statusOk")
        : t("settings.sending.dns.statusPending"),
    ]
      .map(escape)
      .join(",")
  );

  return [header, ...lines].join("\n");
}

/**
 * Lista DNS legível + status + “já copiei” (local + sync tenant LAC-111) + export.
 */
const SendingSetupDnsTable = ({
  records,
  delegationMode = "cname",
  storageScope = "",
  wizardVerifyCta = false,
  verifyBusy = false,
  onMarkAllAndVerify,
}: Props) => {
  const { t } = useTranslation();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const scope = storageScope || "default";
  const [markedCopied, setMarkedCopied] = useState<Set<string>>(
    () => new Set(loadDnsCopiedKeys(scope))
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureUiStateLoaded();
      if (cancelled) {
        return;
      }
      setMarkedCopied(new Set(loadDnsCopiedKeys(scope)));
    })();
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const markCopied = (key: string) => {
    setMarkedCopied((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      void persistDnsCopiedKeys(scope, Array.from(next));
      return next;
    });
  };

  const markAllCopied = (keys: string[]) => {
    setMarkedCopied((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      void persistDnsCopiedKeys(scope, Array.from(next));
      return next;
    });
  };

  const rowKeys = useMemo(
    () => records.map((row, index) => `${row.type}-${row.name}-${index}`),
    [records]
  );

  const pendingDkim = useMemo(
    () => records.filter((row) => isPendingDkim(row)),
    [records]
  );

  if (!records.length) {
    return null;
  }

  const hint =
    delegationMode === "ns"
      ? t("settings.sending.dns.hintNs")
      : t("settings.sending.dns.hint");

  const copyValue = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      markCopied(key);
      toast.success(t("settings.sending.dns.copied"), { autoClose: 2000 });
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      // Carla ainda vê o valor na tabela.
    }
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(recordsAsText(records, t));
      markAllCopied(rowKeys);
      toast.success(t("settings.sending.dns.copiedAll"), { autoClose: 2500 });
    } catch {
      toast.error(t("settings.sending.dns.exportFailed"), { autoClose: 3000 });
    }
  };

  const copyPendingDkim = async () => {
    try {
      await navigator.clipboard.writeText(recordsAsText(pendingDkim, t));
      const keys = records
        .map((row, index) => ({ row, key: `${row.type}-${row.name}-${index}` }))
        .filter(({ row }) => isPendingDkim(row))
        .map(({ key }) => key);
      markAllCopied(keys);
      toast.success(t("settings.sending.dns.copiedPendingDkim"), {
        autoClose: 2500,
      });
    } catch {
      toast.error(t("settings.sending.dns.exportFailed"), { autoClose: 3000 });
    }
  };

  const downloadCsv = () => {
    try {
      const blob = new Blob([recordsAsCsv(records, t)], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "dns-records.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("settings.sending.dns.downloaded"), { autoClose: 2500 });
    } catch {
      toast.error(t("settings.sending.dns.exportFailed"), { autoClose: 3000 });
    }
  };

  const copiedCount = rowKeys.filter((k) => markedCopied.has(k)).length;

  return (
    <Card id="sending-dns">
      <CardHeader className="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <h5 className="card-title mb-1">{t("settings.sending.dns.title")}</h5>
          <p className="text-muted mb-0 small">{hint}</p>
          {storageScope && (
            <p className="text-muted mb-0 small mt-1">
              {t("settings.sending.dns.copiedProgress", {
                done: copiedCount,
                total: records.length,
              })}
            </p>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {pendingDkim.length > 0 && (
            <Button
              color="soft-warning"
              size="sm"
              type="button"
              onClick={copyPendingDkim}
            >
              {t("settings.sending.dns.copyPendingDkim", {
                count: pendingDkim.length,
              })}
            </Button>
          )}
          <Button color="soft-primary" size="sm" type="button" onClick={copyAll}>
            {t("settings.sending.dns.copyAll")}
          </Button>
          <Button color="soft-secondary" size="sm" type="button" onClick={downloadCsv}>
            {t("settings.sending.dns.downloadCsv")}
          </Button>
        </div>
      </CardHeader>
      <CardBody className="pt-0">
        {delegationMode !== "ns" && (
          <Alert color="light" className="border mb-3 py-2 small">
            {t("settings.sending.dns.relativeHostTip")}
          </Alert>
        )}
        <div className="table-responsive">
          <Table className="align-middle mb-0" striped>
            <thead className="table-light">
              <tr>
                <th scope="col" style={{ width: "3rem" }}>
                  {t("settings.sending.dns.status")}
                </th>
                <th scope="col" style={{ width: "3rem" }}>
                  {t("settings.sending.dns.copiedCol")}
                </th>
                <th scope="col">{t("settings.sending.dns.type")}</th>
                <th scope="col">{t("settings.sending.dns.name")}</th>
                <th scope="col">{t("settings.sending.dns.value")}</th>
                <th scope="col">{t("settings.sending.dns.purpose")}</th>
                <th scope="col" className="text-end" />
              </tr>
            </thead>
            <tbody>
              {records.map((row, index) => {
                const key = `${row.type}-${row.name}-${index}`;
                const ok = recordOk(row.status);
                const userCopied = markedCopied.has(key);
                return (
                  <tr key={key}>
                    <td className="text-center">
                      <i
                        className={
                          ok
                            ? "ri-checkbox-circle-fill text-success fs-16"
                            : "ri-time-line text-warning fs-16"
                        }
                        title={
                          ok
                            ? t("settings.sending.dns.statusOk")
                            : t("settings.sending.dns.statusPending")
                        }
                        aria-label={
                          ok
                            ? t("settings.sending.dns.statusOk")
                            : t("settings.sending.dns.statusPending")
                        }
                      />
                    </td>
                    <td className="text-center">
                      <i
                        className={
                          userCopied
                            ? "ri-file-copy-2-fill text-primary fs-16"
                            : "ri-file-copy-2-line text-muted fs-16"
                        }
                        title={
                          userCopied
                            ? t("settings.sending.dns.copiedByYou")
                            : t("settings.sending.dns.notCopiedYet")
                        }
                        aria-label={
                          userCopied
                            ? t("settings.sending.dns.copiedByYou")
                            : t("settings.sending.dns.notCopiedYet")
                        }
                      />
                    </td>
                    <td>
                      <Badge color="secondary" className="bg-secondary-subtle text-secondary">
                        {row.type}
                      </Badge>
                    </td>
                    <td>
                      <code className="text-body">{row.name}</code>
                    </td>
                    <td>
                      <code className="text-body text-break">{row.value}</code>
                    </td>
                    <td>{purposeLabel(row.purpose, t)}</td>
                    <td className="text-end">
                      <Button
                        color="soft-primary"
                        size="sm"
                        type="button"
                        onClick={() => copyValue(row.value, key)}
                      >
                        {copiedKey === key
                          ? t("settings.sending.dns.copied")
                          : t("settings.sending.dns.copy")}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        {wizardVerifyCta && onMarkAllAndVerify ? (
          <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
            <Button
              color="primary"
              type="button"
              disabled={verifyBusy || records.length === 0}
              onClick={() => {
                // Um clique: marca pendentes + verifica (sem linha a linha).
                if (copiedCount < records.length) {
                  markAllCopied(rowKeys);
                }
                onMarkAllAndVerify();
              }}
            >
              {verifyBusy ? (
                <Spinner size="sm" className="me-1" />
              ) : null}
              {copiedCount < records.length
                ? t("settings.sending.wizard.markAllAndVerifyCta")
                : t("settings.sending.wizard.copiedVerifyCta")}
            </Button>
            <span className="text-muted small">
              {copiedCount < records.length
                ? t("settings.sending.wizard.markAllAndVerifyHint", {
                    done: copiedCount,
                    total: records.length,
                  })
                : t("settings.sending.wizard.copiedVerifyHint")}
            </span>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default SendingSetupDnsTable;
